import { computed } from 'vue'
import { env } from '@/utils/env'

interface Consent {
  categories: string[]
}

interface AnalyticsEvent {
  name: string
  params?: Record<string, any>
}

/**
 * Sends Google Ads conversions and GA4 custom events.
 */
export function useGtagConversion(consent: Readonly<{ value: Consent | null }>) {
  const hasMarketing = computed(() =>
    consent.value?.categories?.includes('advertisement') ||
    consent.value?.categories?.includes('marketing')
  )

  const hasAnalytics = computed(() =>
    consent.value?.categories?.includes('analytics')
  )

  // Prevent the same event from being spammed multiple times within <1 sec
  const lastEventTimestamps: Record<string, number> = {}

  function reportConversion({
    sendTo,
    openLink,
    analyticsEvent,
  }: {
    sendTo?: string
    openLink?: () => void
    analyticsEvent?: AnalyticsEvent
  }) {
    const eventKey = analyticsEvent?.name || sendTo

    // Type guard: make sure eventKey is actually a string
    if (typeof eventKey === 'string') {
      const now = Date.now()
      const last = lastEventTimestamps[eventKey] || 0

      if (now - last < 1000) {
        if (env.isDev)
          console.debug(`[useGtagConversion] Ignored duplicate event: ${eventKey}`)
        return
      }

      lastEventTimestamps[eventKey] = now
    }

    // --- Google Ads Conversion ---
    if (hasMarketing.value && sendTo && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: sendTo,
        event_callback: openLink,
      })
      if (env.isDev)
        console.info('[GAds] Conversion event sent:', sendTo)
    }

    // --- Google Analytics Custom Event ---
    if (hasAnalytics.value && analyticsEvent && typeof window.gtag === 'function') {
      window.gtag('event', analyticsEvent.name, analyticsEvent.params || {})
      if (env.isDev)
        console.info('[GA4] Custom event sent:', analyticsEvent.name)
    }
  }

  return { reportConversion }
}
